import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  MenuItem,
  Autocomplete,
  Tooltip,
  FormControl,
  Select,
} from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import UseFetchPurchaseItems from "../../helper/UseFetchPurchaseItems";
import UseFetchSupplierList from "../../helper/UseFetchSupplierList";
import csv_icon from "../../assets/csv_icon.svg";

// Helper date formatter DD/MM/YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return "—";
  const clean = String(dateStr).split("T")[0].split(" ")[0];
  const parts = clean.split("-");
  if (parts.length === 3) {
    return `${parts[2].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[0]}`;
  }
  return dateStr;
};

// Styling for print-friendly report table
const cellStyleHeader = {
  fontSize: "11.5px",
  fontWeight: 700,
  color: "#000",
  padding: "5px 6px",
  border: "1px solid #000",
  backgroundColor: "#f9f9f9",
  whiteSpace: "nowrap",
  fontFamily: "'Roboto', sans-serif",
};

const cellStyleBody = {
  fontSize: "11px",
  color: "#000",
  padding: "4px 6px",
  border: "1px solid #000",
  whiteSpace: "nowrap",
  fontFamily: "'Roboto', sans-serif",
};

const cellStyleTotal = {
  fontSize: "11.5px",
  fontWeight: 700,
  color: "#000",
  padding: "5px 6px",
  border: "1px solid #000",
  backgroundColor: "#f0f0f0",
  fontFamily: "'Roboto', sans-serif",
};

// Parse individual purchase_items record or order record
const parseRowItem = (item) => {
  // If item is order container
  if (item.purchase_items || item.purchase_order_items) {
    const grandTotal = Number(item.grand_total || item.total || 0);
    const subItems = item.purchase_items || item.purchase_order_items || [];
    let taxable = 0;
    let totalCgst = 0;
    let totalSgst = 0;

    subItems.forEach((sub) => {
      const rate = Number(sub.rate || 0);
      const qty = Number(sub.quantity || 0);
      const disc = Number(sub.discount || 0);
      const subTaxable = Math.max(0, rate * qty - disc);
      taxable += subTaxable;
      totalCgst += (subTaxable * Number(sub.cgst || 0)) / 100;
      totalSgst += (subTaxable * Number(sub.sgst || 0)) / 100;
    });

    return {
      billNo: item.bill_number || item.bill_no || item.id || "—",
      dateStr: formatDateDDMMYYYY(item.purchase_date || item.created_at),
      customerName: item.supplier?.name || item.supplier_name || item.customer_name || "—",
      gstinNo: item.gst_no || item.supplier?.gst_no || item.supplier_gstin || item.supplier?.gstin || item.supplier?.gst_number || item.gstin || "—",
      taxableAmount: taxable || Math.max(0, grandTotal - totalCgst - totalSgst),
      cgstAmount: totalCgst,
      sgstAmount: totalSgst,
      grandTotal: grandTotal || taxable + totalCgst + totalSgst,
    };
  }

  // Single purchase item record from /purchase_orders/purchase_items
  const rate = Number(item.rate || 0);
  const qty = Number(item.quantity || 0);
  const disc = Number(item.discount || 0);
  const subtotal = rate * qty;
  const taxableAmount = item.taxable_amount != null ? Number(item.taxable_amount) : Math.max(0, subtotal - disc);

  const cgstPct = Number(item.cgst || 0);
  const sgstPct = Number(item.sgst || 0);

  let cgstVal = item.cgst_amount != null ? Number(item.cgst_amount) : (taxableAmount * cgstPct) / 100;
  let sgstVal = item.sgst_amount != null ? Number(item.sgst_amount) : (taxableAmount * sgstPct) / 100;

  const grandTotal = item.amount != null ? Number(item.amount) : item.total_amount != null ? Number(item.total_amount) : (taxableAmount + cgstVal + sgstVal);

  return {
    billNo: item.bill_number || item.bill_no || item.purchase_order_id || item.id || "—",
    dateStr: formatDateDDMMYYYY(item.purchase_date || item.created_at),
    customerName: item.supplier_name || item.supplier?.name || item.customer_name || "—",
    gstinNo: item.gst_no || item.supplier?.gst_no || item.supplier_gstin || item.supplier?.gstin || item.supplier?.gst_number || item.gstin || "—",
    taxableAmount,
    cgstAmount: cgstVal,
    sgstAmount: sgstVal,
    grandTotal,
  };
};

const InwardSuppliesReport = ({ onClose, onSwitchToSuppliers }) => {
  const componentRef = useRef(null);

  // Filters state: Default Current Month (1st date to End date of current month)
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth(); // 0-indexed

  const firstDayStr = `${year}-${String(month + 1).padStart(2, "0")}-01`;
  const lastDayObj = new Date(year, month + 1, 0);
  const lastDayStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDayObj.getDate()).padStart(2, "0")}`;

  const [startDate, setStartDate] = useState(firstDayStr);
  const [endDate, setEndDate] = useState(lastDayStr);
  const [selectSupplierId, setSelectSupplierId] = useState(null);
  const [page, setPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);



  // Fetch Suppliers List for Filter dropdown
  const { data: supplierListData } = UseFetchSupplierList(1, 100);
  const suppliers = supplierListData?.payload?.suppliers || [];

  // Fetch Purchase Items data (/api/v1/pharmacy/purchase_orders/purchase_items)
  const { data: purchaseItemsResponse, isLoading, isFetching, refetch } = UseFetchPurchaseItems(
    page,
    itemPerPage,
    null,
    startDate,
    endDate
  );



  const rawItems =
    purchaseItemsResponse?.payload?.purchase_items ||
    purchaseItemsResponse?.payload?.purchase_orders ||
    [];

  const filteredItems = selectSupplierId
    ? rawItems.filter(
        (it) =>
          it.supplier_id === selectSupplierId ||
          it.supplier?.id === selectSupplierId
      )
    : rawItems;

  const totalRecords = purchaseItemsResponse?.meta?.total_count || filteredItems.length;
  const totalPage = Math.max(1, Math.ceil(totalRecords / itemPerPage));

  // Compute column totals using parseRowItem
  const totals = filteredItems.reduce(
    (acc, item) => {
      const parsed = parseRowItem(item);
      acc.taxable += parsed.taxableAmount;
      acc.cgst += parsed.cgstAmount;
      acc.sgst += parsed.sgstAmount;
      acc.grandTotal += parsed.grandTotal;
      return acc;
    },
    { taxable: 0, cgst: 0, sgst: 0, grandTotal: 0 }
  );

  // CSV Export Handler
  const handleExportCSV = () => {
    if (!filteredItems || filteredItems.length === 0) return;
    const header = [
      "Bill No.",
      "Date",
      "Customer Name",
      "GSTIN No.",
      "Taxb. Amt.",
      "CGST",
      "SGST",
      "Invoice Value",
    ];

    const csvRows = filteredItems.map((item) => {
      const parsed = parseRowItem(item);
      return [
        `"${parsed.billNo}"`,
        `"${parsed.dateStr}"`,
        `"${parsed.customerName.replace(/"/g, '""')}"`,
        `"${parsed.gstinNo}"`,
        parsed.taxableAmount.toFixed(2),
        parsed.cgstAmount.toFixed(2),
        parsed.sgstAmount.toFixed(2),
        parsed.grandTotal.toFixed(2),
      ];
    });

    const csvContent = [header.join(","), ...csvRows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `inward_supplies_report_${startDate}_to_${endDate}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Quick Date Filters Handler
  const setQuickDateRange = (rangeType) => {
    const now = new Date();
    const formatDateStr = (d) => d.toISOString().slice(0, 10);

    if (rangeType === "today") {
      const todayStr = formatDateStr(now);
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (rangeType === "yesterday") {
      const yest = new Date(now);
      yest.setDate(now.getDate() - 1);
      const yestStr = formatDateStr(yest);
      setStartDate(yestStr);
      setEndDate(yestStr);
    } else if (rangeType === "last7") {
      const past7 = new Date(now);
      past7.setDate(now.getDate() - 6);
      setStartDate(formatDateStr(past7));
      setEndDate(formatDateStr(now));
    }
    setPage(1);
  };

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fff", p: 1, borderRadius: "20px", overflow: "hidden" }}>
      {/* Top Modal Header with Back Button, Title & Red Close Icon */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          pt: 0.5,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {onSwitchToSuppliers && (
            <Tooltip title="Back to Purchase Orders" arrow>
              <IconButton
                onClick={onSwitchToSuppliers}
                size="small"
                sx={{
                  color: "#007A99",
                  border: "1px solid #007A99",
                  borderRadius: "8px",
                  p: "4px",
                  "&:hover": { backgroundColor: "#f0f8fa" },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: "18px" }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: "16px", color: "#111" }}>
            Inward Supplies Report
          </Typography>
        </Box>

        {onClose && (
          <IconButton onClick={onClose} size="small" sx={{ p: "2px" }}>
            <CloseIcon sx={{ color: "red", fontSize: "22px" }} />
          </IconButton>
        )}
      </Box>

      {/* Filter and Action Toolbar (Row 2) */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 1,
          pt: 1,
          pb: 1.5,
          borderBottom: "1px solid #e0e0e0",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            type="date"
            label="From Date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "140px" }}
          />
          <TextField
            size="small"
            type="date"
            label="To Date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "140px" }}
          />

        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuickDateRange("today")}
            sx={{
              fontSize: "11.5px",
              textTransform: "none",
              borderRadius: "6px",
              borderColor: "#007A99",
              color: "#007A99",
              px: "10px",
              py: "3px",
              "&:hover": { backgroundColor: "#f0f8fa" },
            }}
          >
            Today
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuickDateRange("yesterday")}
            sx={{
              fontSize: "11.5px",
              textTransform: "none",
              borderRadius: "6px",
              borderColor: "#007A99",
              color: "#007A99",
              px: "10px",
              py: "3px",
              "&:hover": { backgroundColor: "#f0f8fa" },
            }}
          >
            Yesterday
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setQuickDateRange("last7")}
            sx={{
              fontSize: "11.5px",
              textTransform: "none",
              borderRadius: "6px",
              borderColor: "#007A99",
              color: "#007A99",
              px: "10px",
              py: "3px",
              "&:hover": { backgroundColor: "#f0f8fa" },
            }}
          >
            Last 7 Days
          </Button>
          <Tooltip title="Refresh Data" arrow>
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={isFetching}
              sx={{
                color: "#007A99",
                border: "1px solid #007A99",
                borderRadius: "6px",
                p: "4px",
                "&:hover": { backgroundColor: "#f0f8fa" },
              }}
            >
              <RefreshIcon sx={{ fontSize: "18px" }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Printable Report Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 0,
          bgcolor: "#fff",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <div
          ref={componentRef}
          className="print-container"
          style={{ width: "100%", background: "#fff", padding: "4px 12px 12px 12px", boxSizing: "border-box" }}
        >
          <style>
            {`
              @media print {
                .no-print { display: none !important; }
                html, body {
                  margin: 0 !important;
                  padding: 0 !important;
                  background: #fff !important;
                  width: 100% !important;
                }
                @page {
                  size: portrait;
                  margin: 5mm;
                }
                .print-container {
                  width: 100% !important;
                  max-width: 100% !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  box-sizing: border-box !important;
                }
                .print-table {
                  width: 100% !important;
                  min-width: 100% !important;
                  table-layout: fixed !important;
                  border-collapse: collapse !important;
                }
                .print-table th, .print-table td {
                  padding: 5px 6px !important;
                  box-sizing: border-box !important;
                  font-size: 11px !important;
                }
              }
            `}
          </style>

          {/* Report Header */}
          <Box sx={{ mb: 1 }}>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 1,
                px: 0.5,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: "#000", fontFamily: "sans-serif" }}>
                From Date <b>{formatDateDDMMYYYY(startDate)}</b> to <b>{formatDateDDMMYYYY(endDate)}</b>
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#000", fontFamily: "sans-serif" }}>
                <i>Page No. {page} of {totalPage}</i>
              </Typography>
            </Box>
          </Box>

          {/* Main Table */}
          <TableContainer sx={{ boxShadow: "none", borderRadius: 0, width: "100%", boxSizing: "border-box", overflowX: "auto" }}>
            <Table
              size="small"
              className="print-table"
              sx={{
                width: "100%",
                tableLayout: "fixed",
                borderCollapse: "collapse",
                border: "1px solid #000",
                boxSizing: "border-box",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "left", width: "13%" }}>
                    Bill No.
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "center", width: "13%" }}>
                    Date
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "left", width: "24%" }}>
                    Customer Name
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "left", width: "14%" }}>
                    GSTIN No.
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "right", width: "12%" }}>
                    Taxb. Amt.
                  </TableCell>
                  {/* IGST Column skipped as explicitly requested by user */}
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "right", width: "7%" }}>
                    CGST
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "right", width: "7%" }}>
                    SGST
                  </TableCell>
                  <TableCell sx={{ ...cellStyleHeader, textAlign: "right", width: "10%" }}>
                    Invoice Value
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={8} sx={{ padding: "6px", border: "1px solid #000" }}>
                        <Skeleton height={24} />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      sx={{
                        ...cellStyleBody,
                        textAlign: "center",
                        py: 4,
                        fontSize: "13px",
                      }}
                    >
                      No Inward Supplies records found for the selected period.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item, idx) => {
                    const parsed = parseRowItem(item);

                    return (
                      <TableRow key={item.id || idx}>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "left" }}>
                          {parsed.billNo}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "center" }}>
                          {parsed.dateStr}
                        </TableCell>
                        <TableCell
                          sx={{
                            ...cellStyleBody,
                            textAlign: "left",
                            textTransform: "uppercase",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={parsed.customerName}
                        >
                          {parsed.customerName}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "left" }}>
                          {parsed.gstinNo}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "right" }}>
                          {parsed.taxableAmount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "right" }}>
                          {parsed.cgstAmount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "right" }}>
                          {parsed.sgstAmount.toFixed(2)}
                        </TableCell>
                        <TableCell sx={{ ...cellStyleBody, textAlign: "right", fontWeight: 600 }}>
                          {parsed.grandTotal.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}

                {/* Summary Totals Row */}
                {filteredItems.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ ...cellStyleTotal, textAlign: "right" }}>
                      Total:
                    </TableCell>
                    <TableCell sx={{ ...cellStyleTotal, textAlign: "right" }}>
                      {totals.taxable.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ ...cellStyleTotal, textAlign: "right" }}>
                      {totals.cgst.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ ...cellStyleTotal, textAlign: "right" }}>
                      {totals.sgst.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ ...cellStyleTotal, textAlign: "right" }}>
                      {totals.grandTotal.toFixed(2)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Box>

      {/* Pagination Footer (Hidden during Print) */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          pt: 1,
          px: 1,
          pb: 0.5,
          borderTop: "1px solid #e0e0e0",
          mt: 0.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        {/* Right Pagination Controls */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            startIcon={<ArrowBackIcon sx={{ fontSize: "14px !important" }} />}
            sx={{
              fontSize: "12px",
              textTransform: "capitalize",
              color: page === 1 ? "grey" : "#007A99",
              minWidth: "auto",
            }}
          >
            Prev
          </Button>

          {(() => {
            const pages = [];
            if (totalPage <= 7) {
              for (let i = 1; i <= totalPage; i++) pages.push(i);
            } else {
              pages.push(1);
              if (page > 3) pages.push("...");
              for (let i = Math.max(2, page - 1); i <= Math.min(totalPage - 1, page + 1); i++) pages.push(i);
              if (page < totalPage - 2) pages.push("...");
              pages.push(totalPage);
            }
            return pages.map((p, i) =>
              p === "..." ? (
                <Typography key={`ellipsis-${i}`} sx={{ fontSize: "12px", color: "#888", px: "2px", alignSelf: "center" }}>…</Typography>
              ) : (
                <Button
                  key={p}
                  onClick={() => setPage(p)}
                  sx={{
                    fontSize: "12px",
                    minWidth: "30px",
                    height: "30px",
                    padding: "2px",
                    borderRadius: "6px",
                    color: page === p ? "#fff" : "#333",
                    backgroundColor: page === p ? "#007A99" : "#fff",
                    border: "1px solid #d0d0d0",
                    "&:hover": { backgroundColor: page === p ? "#007A99" : "#f2f2f2" },
                  }}
                >
                  {p}
                </Button>
              )
            );
          })()}

          <Button
            onClick={() => setPage((p) => Math.min(totalPage, p + 1))}
            disabled={page >= totalPage}
            endIcon={<ArrowForwardIcon sx={{ fontSize: "14px !important" }} />}
            sx={{
              fontSize: "12px",
              textTransform: "capitalize",
              color: page >= totalPage ? "grey" : "#007A99",
              minWidth: "auto",
            }}
          >
            Next
          </Button>

          {/* Jump to Page */}
          <Box display="flex" alignItems="center" gap="4px" ml={0.5}>
            <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "nowrap" }}>Go to</Typography>
            <TextField
              size="small"
              type="number"
              inputProps={{ min: 1, max: totalPage, style: { padding: "2px 6px", fontSize: "12px", width: "44px", textAlign: "center" } }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", height: "30px" } }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const val = parseInt(e.target.value, 10);
                  if (val >= 1 && val <= totalPage) {
                    setPage(val);
                    e.target.value = "";
                  }
                }
              }}
            />
          </Box>

          {/* Rows per page */}
          <FormControl size="small" sx={{ ml: 0.5, minWidth: "80px" }}>
            <Select
              value={itemPerPage}
              onChange={(e) => {
                setItemPerPage(e.target.value);
                setPage(1);
              }}
              displayEmpty
              sx={{ height: "30px", fontSize: "12px", borderRadius: "6px" }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
    </Box>
  );
};

export default InwardSuppliesReport;
